<template>
  <div class="general-pool">
    <div class="pool-toolbar">
      <div class="pool-toolbar__left">
        <el-input
          v-model="searchKeyword"
          placeholder="搜索标签名称/ID"
          prefix-icon="el-icon-search"
          clearable
          size="medium"
          style="width: 300px"
        ></el-input>
        <el-select v-model="typeFilter" @change="refresh" filterable clearable placeholder="全部" size="medium" style="width: 120px">
          <el-option label="全部" value=""></el-option>
          <el-option v-for="item in businessTypeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
        </el-select>
        <span class="source-count">共 {{ filteredTags.length }} 个标签</span>
      </div>
      <div class="pool-toolbar__right" v-if="this.$hasPerm('B008&3-2')">
        <el-button type="primary" icon="el-icon-plus" @click="openAddDialog">新增标签</el-button>
      </div>
    </div>

    <div class="pool-table-header">
      <div class="pool-table-header__col pool-table-header__col--30">
        <span class="pool-table-header__rank">序号</span>
        <span>标签名称 / 主标签</span>
      </div>
      <div class="pool-table-header__col pool-table-header__col--20">业务类型</div>
      <div class="pool-table-header__col pool-table-header__col--15">状态</div>
      <div v-if="this.$hasPerm('B008&3-2')" class="pool-table-header__col pool-table-header__col--35 pool-table-header__col--right">ID / 标签描述 / 操作</div>
      <div v-else class="pool-table-header__col pool-table-header__col--35 pool-table-header__col--right">ID / 标签描述</div>
    </div>

    <div class="pool-list">
      <tag-card
        v-for="(tag, idx) in filteredTags"
        :key="tag.id"
        :tag="tag"
        :index="idx"
        pool-type="general"
        :sort-mode="false"
        :parent-tag-name="getParentName(tag.parentTagId)"
        @edit="openEditDialog"
        @delete="deleteTag"
        @assign-company="openAssignCompany"
      ></tag-card>
    </div>

    <tag-form-dialog
      ref="tagFormDialog"
      :pool-type="'general'"
      :all-tags="tags"
      :edit-tag="currentEditTag"
      :case-status-option="caseStatusOption"
      :contact-status-option="contactStatusOption"
      :filtered-tags="filteredTags"
      @confirm="onFormConfirm"
    ></tag-form-dialog>

    <assign-company-dialog
      ref="assignCompanyDialog"
      :general-tags="tags"
      :companies="companyList"
      @assigned="refresh"
    ></assign-company-dialog>
  </div>
</template>

<script>
import TagCard from './tag-card';
import TagFormDialog from './tag-form-dialog';
import AssignCompanyDialog from './assign-company-dialog';
import TagPoolMixin from './tag-pool-mixin';
import { BUSINESS_TYPE_OPTIONS } from '../../../utils/business-type';

export default {
  name: 'GeneralPool',
  components: { TagCard, TagFormDialog, AssignCompanyDialog },
  // 复用标签池公共逻辑（删除标签、上下文校验）
  mixins: [TagPoolMixin],
  props: {
    tags: { type: Array, default: () => [] },
    // 公司列表，透传给「指定标签分配到公司」弹窗作为目标公司数据源
    companyList: { type: Array, default: () => [] },
    // 是否处于通用标签池 tab（父级点击该 tab 时置为 true）
    active: { type: Boolean, default: false },
    caseStatusOption: { type: Array, default: () => [] },
    contactStatusOption: { type: Array, default: () => [] }
  },
  data() {
    return {
      businessTypeOptions: BUSINESS_TYPE_OPTIONS,
      searchKeyword: '',
      typeFilter: '',
      currentEditTag: null
    };
  },
  watch: {
    // 每次切换回通用标签池时重置搜索与业务类型筛选，并刷新数据；
    // 首次进入由父级 mounted 的 fetchGeneralTags 负责，这里不 immediate 以避免重复请求
    active(val) {
      if (val) this.enterGeneral();
    }
  },
  computed: {
    filteredTags() {
      let list = this.tags;
      if (this.searchKeyword) {
        const kw = this.searchKeyword.toLowerCase();
        list = list.filter(t =>
          String(t.name || '').toLowerCase().includes(kw) ||
          String(t.id || '').toLowerCase().includes(kw) ||
          String(t.code || '').toLowerCase().includes(kw)
        );
      }
      if (this.typeFilter) {
        const typeOption = this.businessTypeOptions.find(o => o.value === this.typeFilter);
        const typeLabel = typeOption ? typeOption.label : '';
        list = list.filter(t => t.type === this.typeFilter || (typeLabel && t.type === typeLabel));
      }
      return list;
    }
  },
  methods: {
    getParentName(parentId) {
      if (!parentId) return null;
      const parent = this.tags.find(t => t.id === parentId);
      return parent ? parent.name : null;
    },
    openAddDialog() {
      this.currentEditTag = null;
      this.$refs.tagFormDialog.open();
    },
    openEditDialog(tag) {
      this.currentEditTag = tag;
      this.$refs.tagFormDialog.open();
    },
    onFormConfirm() {
      this.currentEditTag = null;
      this.refresh();
    },
    // 通用池不依赖归属公司与策略组上下文，无需前置校验
    validateBatchContext() {
      return '';
    },
    // 通用池不属于任何公司 / 策略组，companyId、groupId 固定传 0
    getOperationContext() {
      return { companyId: 0, groupId: 0 };
    },
    // 删除成功后的刷新：通知父级重新拉取通用标签
    onTagDeleted() {
      this.refresh();
    },
    openAssignCompany(tag) {
      // 传明细 id，与分配接口的 detailIds 入参口径保持一致
      this.$refs.assignCompanyDialog.open(tag.detailId);
    },
    // 进入通用标签池（首次进入或再次进入）时执行：重置筛选条件并刷新数据
    enterGeneral() {
      this.searchKeyword = '';
      this.typeFilter = '';
      this.refresh();
    },
    refresh() {
      this.$emit('refresh', this.typeFilter || '');
    }
  }
};
</script>

<style lang="less" scoped>
@import '../../../assets/style/variable.less';

.pool-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
  }
}

.pool-table-header {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 12px 20px;
  font-size: 12px;
  font-weight: bold;
  color: #64748b;
  margin-bottom: 4px;

  &__col {
    display: flex;
    align-items: center;

    &--30 { width: 30%; }
    &--15 { width: 15%; justify-content: center; }
    &--20 { width: 20%; justify-content: center; }
    &--35 { width: 35%; }
    &--right { justify-content: flex-end; }
  }

  &__rank {
    width: 64px;
    flex-shrink: 0;
  }
}

.pool-list {
  min-height: 200px;
}

.source-count {
  font-size: 12px;
  color: #c0c4cc;
}
</style>



// WEBPACK FOOTER //
// src/page/main/tagpools/general-pool.vue